import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Inbox,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  AlertTriangle,
  Sparkles,
  Copy,
  Check,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { Button } from "../components/Button";
import { useMe } from "../lib/me";
import { api } from "../lib/api";
import type { DashboardStats } from "../lib/me";

export default function Dashboard() {
  const { me } = useMe();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    api.get<DashboardStats>("/api/stats").then(setStats).catch(() => setStats(null));
  }, []);

  const tenant = me.tenant!;
  const inboundEmail = `support@${tenant.inboundSubdomain}`;
  const remaining = Math.max(0, tenant.quotaEmailsLimit - tenant.quotaEmailsUsed);
  const daysLeft = tenant.trialExpiresAt
    ? Math.max(0, Math.ceil((new Date(tenant.trialExpiresAt).getTime() - Date.now()) / 86400000))
    : null;

  const copy = async () => {
    await navigator.clipboard.writeText(inboundEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-7 animate-slide-up">
      {/* Header and Welcome */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            Welcome back, {tenant.displayName}
          </h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            Here's what is happening today. {tenant.settings.autoRepliesEnabled
              ? "Auto-replies are active."
              : "Auto-replies are off — drafts are queued for your approval."}
          </p>
        </div>
      </div>

      {/* Inbound address panel */}
      <Card className="overflow-hidden border-brand-200/50 bg-linear-to-r from-brand-50/50 via-indigo-50/10 to-transparent">
        <CardBody className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-brand-600 bg-brand-50 px-2 py-0.5 rounded-md border border-brand-100">
                Inbound support address
              </span>
              <p className="text-sm font-semibold text-slate-500">
                Forward your customer support emails to this unique address:
              </p>
              <div className="pt-1.5 break-all font-mono text-base font-bold text-slate-900">
                {inboundEmail}
              </div>
            </div>
            <Button variant="secondary" size="md" onClick={copy} className="self-start sm:self-center shrink-0">
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-emerald-600" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 text-slate-500" />
                  <span>Copy Address</span>
                </>
              )}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Trial notification */}
      {tenant.plan === "trial" && (
        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/60 p-5 shadow-2xs backdrop-blur-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-950">
                Free Trial — {remaining} emails remaining{daysLeft != null && `, expires in ${daysLeft} days`}
              </p>
              <p className="mt-0.5 text-sm text-amber-800/80 leading-normal">
                Test the flow by sending an email to your inbound address. You'll see drafts generated instantly.
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate("/settings")}
              className="bg-amber-600 hover:bg-amber-750 text-white border-0 shadow-xs"
            >
              Request Upgrade
            </Button>
          </div>
        </div>
      )}

      {/* Quota stripe for paid plans */}
      {tenant.plan !== "trial" && (
        <QuotaStripe
          used={tenant.quotaEmailsUsed}
          limit={tenant.quotaEmailsLimit}
          remaining={remaining}
        />
      )}

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Received today"
          value={stats?.today.received ?? "—"}
          icon={Inbox}
          colorClass="text-brand-500 bg-brand-50 border-brand-100/50"
        />
        <StatCard
          label="Auto-replied today"
          value={stats?.today.autoReplied ?? "—"}
          meta={stats && stats.today.received > 0 ? `${Math.round(stats.today.autoRepliedRate * 100)}% Auto` : undefined}
          icon={CheckCircle2}
          colorClass="text-emerald-600 bg-emerald-50 border-emerald-100/50"
        />
        <StatCard
          label="Awaiting review"
          value={stats?.open.awaitingReview ?? "—"}
          icon={Clock}
          colorClass="text-amber-500 bg-amber-50 border-amber-100/50"
        // highlight={!!(stats && stats.open.awaitingReview > 0)}
        />
        <StatCard
          label="Escalated today"
          value={stats?.today.escalated ?? "—"}
          icon={ArrowUpRight}
          colorClass="text-rose-500 bg-rose-50 border-rose-100/50"
        />
      </div>

      {/* Middle Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Categories Bar Chart */}
        <Card>
          <CardHeader
            title="Top categories today"
            description="Overview of classified support tickets"
            actions={
              <Link to="/categories">
                <Button size="sm" variant="ghost" className="text-xs font-bold text-slate-500 hover:text-slate-900">
                  Manage categories <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                </Button>
              </Link>
            }
          />
          <CardBody>
            {!stats || stats.topCategories.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="No categories classified today"
                hint="As classified emails are processed, ticket distribution will be graphed here."
              />
            ) : (
              <ul className="space-y-4">
                {stats.topCategories.map((cat) => {
                  const max = stats.topCategories[0]?.count ?? 1;
                  const pct = (cat.count / max) * 100;
                  return (
                    <li key={cat.intentId} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                        <span className="truncate">{cat.name}</span>
                        <span className="text-slate-900">{cat.count}</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-linear-to-r from-brand-500 to-indigo-500 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Recent Escalations */}
        <Card>
          <CardHeader
            title="Recent escalations"
            description="Cases flagged for human intervention"
            actions={
              <Link to="/cases">
                <Button size="sm" variant="ghost" className="text-xs font-bold text-slate-500 hover:text-slate-900">
                  All cases <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                </Button>
              </Link>
            }
          />
          <CardBody>
            {!stats || stats.recentEscalations.length === 0 ? (
              <EmptyState
                icon={ArrowUpRight}
                title="No recent escalations"
                hint="If the AI cannot resolve a case, it will show up here with detailed context."
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.recentEscalations.map((esc) => (
                  <li key={esc.caseId} className="py-3 first:pt-0 last:pb-0">
                    <Link to={`/cases/${esc.caseId}`} className="group block hover:bg-slate-50/80 -mx-3 px-3 py-2 rounded-xl transition-colors">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="truncate text-sm font-bold text-slate-900 group-hover:text-brand-600 transition-colors">
                          {esc.subject}
                        </p>
                        <span className="shrink-0 text-xs font-medium text-slate-400">
                          {new Date(esc.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                        <span className="truncate">{esc.fromEmail}</span>
                        {esc.intentName && (
                          <span className="shrink-0 bg-slate-100 text-slate-650 px-2 py-0.5 rounded-md font-semibold text-[10px]">
                            {esc.intentName}
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Needs Attention Panel */}
      <AttentionPanel stats={stats} />
    </div>
  );
}

function AttentionPanel({ stats }: { stats: DashboardStats | null }) {
  const { me } = useMe();
  const navigate = useNavigate();
  const tenant = me.tenant!;
  const settings = tenant.settings;

  type Item = {
    id: string;
    tone: "amber" | "gray";
    title: string;
    hint: string;
    cta: string;
    onClick: () => void;
  };
  const items: Item[] = [];

  if (stats && stats.counts.activeIntents === 0) {
    items.push({
      id: "categories",
      tone: "amber",
      title: "Add support categories",
      hint: "Configure intents so the AI can classify inbound customer emails. Start with a blank canvas or starter packs.",
      cta: "Create intent",
      onClick: () => navigate("/categories/new"),
    });
  }

  if (settings.senderEmail && settings.senderEmailStatus === "pending") {
    items.push({
      id: "sender_pending",
      tone: "amber",
      title: "Verify custom sender email",
      hint: `A verification link was sent to ${settings.senderEmail}. You must approve it so replies look native.`,
      cta: "Verify settings",
      onClick: () => navigate("/settings"),
    });
  }

  if (settings.dryRunMode && stats && stats.counts.activeIntents > 0) {
    items.push({
      id: "dry_run",
      tone: "amber",
      title: "Dry-run mode is currently enabled",
      hint: "Replies are generated but NOT sent to users. Switch Dry-run off to allow drafts to go out.",
      cta: "Disable dry-run",
      onClick: () => navigate("/settings"),
    });
  }

  if (
    !settings.autoRepliesEnabled &&
    !settings.dryRunMode &&
    stats &&
    stats.counts.activeIntents > 0
  ) {
    items.push({
      id: "auto_replies",
      tone: "gray",
      title: "Automate high-confidence categories",
      hint: "Change the intent's reply mode to Direct and toggle Auto-replies on to let the AI respond autonomously.",
      cta: "Manage settings",
      onClick: () => navigate("/settings"),
    });
  }

  if (settings.escalationDestination.type === "review_queue_only") {
    items.push({
      id: "escalation",
      tone: "gray",
      title: "Set up escalation fallback",
      hint: "Decide where unresolved tickets are forwarded, or keep them in the review queue.",
      cta: "Configure destination",
      onClick: () => navigate("/settings"),
    });
  }

  return (
    <Card className="border-slate-200">
      <CardHeader
        title="Checklist & Setup Actions"
        description={
          items.length === 0
            ? "Your operator configuration is fully optimized."
            : "Review recommended actions to get the most out of Triage."
        }
      />
      <CardBody>
        {items.length === 0 ? (
          <div className="flex items-center gap-3 py-1.5 text-sm font-semibold text-emerald-800 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-150 text-emerald-700">
              <Check className="h-4.5 w-4.5" />
            </div>
            All tasks completed. Triage is active and monitoring support email.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 space-y-1">
            {items.map((item) => (
              <AttentionItem
                key={item.id}
                tone={item.tone}
                title={item.title}
                hint={item.hint}
                cta={item.cta}
                onClick={item.onClick}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function StatCard({
  label,
  value,
  meta,
  icon: Icon,
  colorClass,
  highlight,
}: {
  label: string;
  value: number | string;
  meta?: string;
  icon: typeof Inbox;
  colorClass: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? "ring-2 ring-amber-500/50 shadow-md" : ""}>
      <CardBody className="p-5 flex flex-col justify-between h-32">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
          <div className={`flex h-8 w-8 items-center justify-center rounded-lg border ${colorClass}`}>
            <Icon className="h-4.5 w-4.5" />
          </div>
        </div>
        <div className="mt-auto flex items-baseline justify-between">
          <span className="text-3xl font-bold tracking-tight text-slate-900">{value}</span>
          {meta && (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-100/50">
              <TrendingUp className="h-2.5 w-2.5" /> {meta}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function EmptyState({
  icon: Icon,
  title,
  hint,
}: {
  icon: typeof Inbox;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center px-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 border border-slate-200/60 text-slate-400">
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-3.5 text-sm font-bold text-slate-905">{title}</p>
      <p className="mt-1 max-w-xs text-xs font-medium text-slate-500 leading-normal">{hint}</p>
    </div>
  );
}

function AttentionItem({
  tone,
  title,
  hint,
  cta,
  onClick,
}: {
  tone: "amber" | "gray";
  title: string;
  hint: string;
  cta: string;
  onClick?: () => void;
}) {
  return (
    <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <div className={`flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-lg border mt-0.5 ${tone === "amber" ? "bg-amber-50 text-amber-600 border-amber-100" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="space-y-0.5">
          <p className="text-sm font-bold text-slate-900">{title}</p>
          <p className="text-xs font-medium text-slate-500 leading-normal">{hint}</p>
        </div>
      </div>
      <Button size="sm" variant="secondary" onClick={onClick} className="shrink-0 self-start sm:self-center">
        {cta}
      </Button>
    </li>
  );
}

function QuotaStripe({ used, limit, remaining }: { used: number; limit: number; remaining: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const exhausted = remaining === 0;
  const near = pct >= 80 && !exhausted;

  const tone = exhausted
    ? { wrap: "border-rose-200 bg-rose-50/80", strong: "text-rose-900", text: "text-rose-800", bar: "bg-rose-500" }
    : near
      ? { wrap: "border-amber-200 bg-amber-50/80", strong: "text-amber-900", text: "text-amber-800", bar: "bg-amber-500" }
      : { wrap: "border-slate-205/60 bg-white", strong: "text-slate-900", text: "text-slate-500", bar: "bg-brand-500" };

  return (
    <Card className={`overflow-hidden border ${tone.wrap}`}>
      <CardBody className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className={`text-sm font-bold ${tone.strong}`}>
            Monthly usage quota — {used.toLocaleString()} of {limit.toLocaleString()} emails
          </p>
          <p className={`text-xs font-semibold ${tone.text}`}>{remaining.toLocaleString()} remaining</p>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full transition-all duration-500 ${tone.bar}`} style={{ width: `${pct}%` }} />
        </div>
        {exhausted && (
          <p className={`mt-2.5 text-xs font-semibold ${tone.text}`}>
            Inbound processing is paused. Upgrade your package or contact{" "}
            <a href="mailto:hello@appgambit.com" className="font-bold underline">hello@appgambit.com</a> to increase quota limits.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
